import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Clock } from 'lucide-react';
import { blogPosts } from '../data/mockData';
import SEO from './SEO';
import { breadcrumbSchema } from '../utils/structuredData';

const Blog = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'All' },
    { id: 'updates', label: 'Updates' },
    { id: 'news', label: 'News' },
    { id: 'stories', label: 'Stories' },
    { id: 'insights', label: 'Insights' }
  ];

  const filteredPosts = activeFilter === 'all'
    ? blogPosts
    : blogPosts.filter(post => post.category === activeFilter);

  const breadcrumbs = breadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' }
  ]);

  return (
    <div className="min-h-screen bg-black text-white">
      <SEO
        title="Journal | Infinite Reyn Productions - Updates, News, Stories, Insights"
        description="Updates, news, stories, and insights from across our three verticals: Books by Evie Montclaire, Music by Divine Reyn, and Theatre productions. Discover behind-the-scenes content and creative insights."
        canonical="/blog"
        keywords="Infinite Reyn Productions blog, creative writing insights, music production updates, theatre news, Evie Montclaire, Divine Reyn, paranormal romance updates, worship music news"
        structuredData={breadcrumbs}
      />
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-8 left-8 z-50 flex items-center gap-2 text-white/80 hover:text-white transition-colors group"
      >
        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
        <span className="text-sm tracking-wider">BACK</span>
      </button>

      {/* Page Indicator */}
      <div className="fixed top-8 right-8 z-50">
        <span className="text-xs tracking-[0.3em] text-gold">BLOG</span>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-32">
        {/* Header */}
        <div className="text-center mb-20">
          <h1 className="text-5xl md:text-6xl font-light mb-6 tracking-wide">
            Journal
          </h1>
          <div className="w-24 h-px bg-gold mx-auto mb-8"></div>
          <p className="text-lg text-white/70 font-light leading-relaxed max-w-2xl mx-auto">
            Updates, news, stories, and insights from across our three verticals.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-6 mb-16">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setActiveFilter(category.id)}
              className={`text-sm tracking-[0.2em] pb-2 transition-all relative ${
                activeFilter === category.id
                  ? 'text-white'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              {category.label.toUpperCase()}
              {activeFilter === category.id && (
                <span className="absolute bottom-0 left-0 right-0 h-px bg-gold"></span>
              )}
            </button>
          ))}
        </div>

        {/* Blog Posts Grid */}
        <div className="space-y-16">
          {filteredPosts.map((post, index) => (
            <article
              key={post.id}
              className="group border-b border-white/10 pb-16 last:border-0"
            >
              <Link to={`/blog/${post.id}`}>
                <div className="grid md:grid-cols-5 gap-8 items-start">
                  {/* Featured Image */}
                  <div className="md:col-span-2">
                    <div className="relative overflow-hidden aspect-video bg-zinc-900 flex items-center justify-center">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  </div>

                  {/* Post Info */}
                  <div className="md:col-span-3 pt-2">
                    {/* Meta */}
                    <div className="flex items-center gap-4 mb-4 text-xs text-white/50">
                      <span className="tracking-[0.2em] text-gold uppercase">
                        {post.category}
                      </span>
                      <span>•</span>
                      <span>{post.date}</span>
                      <span>•</span>
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{post.readTime}</span>
                      </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-3xl font-light mb-4 leading-tight group-hover:text-gold transition-colors">
                      {post.title}
                    </h2>

                    {/* Author */}
                    <p className="text-sm text-white/50 mb-4">
                      By {post.author}
                    </p>

                    {/* Excerpt */}
                    <p className="text-white/70 leading-relaxed mb-6">
                      {post.excerpt}
                    </p>

                    {/* Read More Link */}
                    <span className="inline-flex items-center gap-2 text-sm tracking-wider text-gold group-hover:gap-3 transition-all">
                      READ MORE
                      <span className="text-lg">→</span>
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          ))}
        </div>

        {/* Empty State */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-white/50 text-lg">
              No posts in this category yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;